import { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';

interface BottomSheetProps {
  children: PropsWithChildren<React.ReactNode>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
const BottomSheet = ({ children, isOpen, setIsOpen }: BottomSheetProps) => {
  const [startY, setStartY] = useState<number | null>(null);
  const [currentY, setCurrentY] = useState<number | null>(null);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.pointerEvents = 'auto';
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (startY !== null && currentY !== null) {
      const distance = currentY - startY;
      const threshold = window.innerHeight * 0.2;

      if (distance > threshold) {
        setIsOpen(false);
      }
    }
    setStartY(null);
    setCurrentY(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setStartY(e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (startY !== null) {
      setCurrentY(e.clientY);
    }
  };

  const handleMouseUp = () => {
    if (startY !== null && currentY !== null) {
      const distance = currentY - startY;
      const threshold = window.innerHeight * 0.2;

      if (distance > threshold) {
        setIsOpen(false);
      }
    }
    setStartY(null);
    setCurrentY(null);
  };

  return (
    <Background
      onClick={() => {
        if (isOpen) {
          setIsOpen(false);
        }
      }}
      $isOpen={isOpen}
    >
      <BottomSheetContainer
        $isOpen={isOpen}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <BottomSheetContent>
          <Scroll>{children}</Scroll>
        </BottomSheetContent>
      </BottomSheetContainer>
    </Background>
  );
};

const Background = styled.div<{ $isOpen: boolean }>`
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.8s ease-in-out;
  margin: 0 auto;
  background: ${({ $isOpen }) =>
    $isOpen ? 'rgba(0, 0, 0, 0.6)' : 'transparent'};
  width: 496px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ $isOpen }) => ($isOpen ? 99 : -1)};
`;

const BottomSheetContainer = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  position: fixed;
  bottom: ${({ $isOpen }) => ($isOpen ? 0 : '-50%')};
  left: 0;
  right: 0;
  border-top-left-radius: 1.25rem;
  border-top-right-radius: 1.25rem;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.6);
  height: 15.375rem;
  box-sizing: border-box;
  background: white;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  transition: bottom 0.55s ease-in-out;
  z-index: 99;
  body.isDesktop & {
    width: 496px;
    max-width: 90%; /* 화면이 좁아질 때 유연하게 축소 */
    margin: 0 auto;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const BottomSheetContent = styled.div`
  padding-top: 1.5rem;

  position: relative;
`;

const Scroll = styled.div`
  height: inherit;
  overflow-y: auto;
  padding-bottom: 5.25rem;
  &::-webkit-scrollbar {
    display: none;
  }

  &::after {
    position: absolute;
    bottom: 0;
    left: 0;
    content: '';
    display: block;
    width: 100%;
    background: white;
    height: 1.75rem;
  }
`;

export default BottomSheet;
